import React, { useEffect, useMemo, useRef, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import ColorSwatch from "./ColorSwatch";
import { KIND_LABEL, KIND_PLURAL, createTaxonomy, colorForName } from "./taxonomyApi";

/**
 * "Add a tag" drawer — type a name, press Enter, repeat, Save.
 *
 * The one rule that matters: ENTER is the only separator. A tag may contain
 * spaces AND commas ("Banking & Financial Services", "SOC 2, Type II"), so
 * splitting on either would quietly shred half the vocabulary. Whatever is
 * still in the input when Save is pressed counts as a final entry, because
 * typing a name and hitting Save without Enter is the obvious thing to do.
 *
 * Each pending entry gets a colour immediately — deterministic from the name,
 * matching what the server would assign — and the swatch is editable before
 * saving as well as after.
 *
 * Props:
 *   open      bool
 *   kind      "industry" | "compliance" | "certification"
 *   onClose   () => void
 *   onSaved   (result) => void   fired after a successful POST
 */
export default function AddTagDrawer({ open, kind, onClose, onSaved }) {
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState([]); // [{ name, color }]
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  // Reset between openings so a previous batch never leaks into the next one.
  useEffect(() => {
    if (open) {
      setDraft("");
      setPending([]);
      setError("");
    }
  }, [open, kind]);

  const label = KIND_LABEL[kind] || "Tag";
  const plural = KIND_PLURAL[kind] || "Tags";

  /** Same normalisation the server applies, so the preview cannot lie. */
  const normalize = (raw) => String(raw ?? "").replace(/\s+/g, " ").trim();
  const slugOf = (name) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const pendingSlugs = useMemo(
    () => new Set(pending.map((p) => slugOf(p.name))),
    [pending],
  );

  /** Commit the input as one entry. Returns false when there was nothing usable. */
  const commitDraft = (value) => {
    const name = normalize(value);
    if (!name) return false;
    const slug = slugOf(name);
    if (!slug || pendingSlugs.has(slug)) {
      // Already staged — clear the box rather than stacking a duplicate.
      setDraft("");
      return false;
    }
    setPending((prev) => [...prev, { name, color: colorForName(name) }]);
    setDraft("");
    return true;
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitDraft(draft);
      return;
    }
    // Backspace on an empty box pulls the last chip back for editing — the
    // standard chip-input affordance.
    if (e.key === "Backspace" && !draft && pending.length) {
      const last = pending[pending.length - 1];
      setPending((prev) => prev.slice(0, -1));
      setDraft(last.name);
    }
  };

  const removeAt = (index) => setPending((prev) => prev.filter((_, i) => i !== index));

  const recolorAt = (index, color) =>
    setPending((prev) => prev.map((p, i) => (i === index ? { ...p, color } : p)));

  const save = async () => {
    // Anything still in the box counts. Build the final list locally rather
    // than relying on a setState that has not flushed yet.
    const trailing = normalize(draft);
    const batch = [...pending];
    if (trailing && !pendingSlugs.has(slugOf(trailing))) {
      batch.push({ name: trailing, color: colorForName(trailing) });
    }
    if (!batch.length) {
      setError(`Type a ${label.toLowerCase()} name first.`);
      return;
    }

    setSaving(true);
    setError("");
    try {
      // Names go over as an array — one element per entry, never joined.
      const result = await createTaxonomy(
        kind,
        batch.map((b) => b.name),
      );

      // The bulk endpoint assigns a colour per name; apply any the admin
      // changed here as follow-up patches so one failure cannot lose the
      // whole batch. Imported lazily to avoid a cycle with taxonomyApi.
      const { updateTaxonomy } = await import("./taxonomyApi");
      const rows = result?.rows || [];
      const bySlug = new Map(rows.map((r) => [r.slug, r]));
      await Promise.all(
        batch.map(async (entry) => {
          const row = bySlug.get(slugOf(entry.name));
          if (!row) return;
          if (String(row.color).toLowerCase() === String(entry.color).toLowerCase()) return;
          try {
            await updateTaxonomy(kind, row.id, { color: entry.color });
          } catch {
            // A colour that did not stick is cosmetic — the tag exists.
          }
        }),
      );

      setDraft("");
      setPending([]);
      onSaved?.(result);
      onClose?.();
    } catch (err) {
      setError(err?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const total = pending.length + (normalize(draft) ? 1 : 0);

  return (
    <Drawer anchor="right" open={open} onClose={saving ? undefined : onClose}>
      <Box sx={{ width: 380, p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
        <Typography variant="h6">Add {plural.toLowerCase()}</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Press Enter after each one. Spaces and commas are part of the name.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <TextField
          inputRef={inputRef}
          fullWidth
          autoFocus
          size="small"
          label={`${label} name`}
          placeholder="e.g. Banking &amp; Financial Services"
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
        />

        {pending.length > 0 && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.75, overflowY: "auto" }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              {pending.length} ready to save
            </Typography>

            {pending.map((entry, index) => (
              <Box
                key={`${entry.name}-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  border: "1px solid rgba(0,0,0,0.12)",
                }}
              >
                <ColorSwatch
                  value={entry.color}
                  disabled={saving}
                  onChange={(hex) => recolorAt(index, hex)}
                />
                <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-word" }}>
                  {entry.name}
                </Typography>
                <Button
                  size="small"
                  color="inherit"
                  disabled={saving}
                  onClick={() => removeAt(index)}
                  sx={{ minWidth: 0, px: 1, color: "text.secondary" }}
                >
                  ✕
                </Button>
              </Box>
            ))}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ flex: 1 }} />

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving || total === 0}>
            {saving ? "Saving…" : `Save${total ? ` ${total}` : ""}`}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
